import { fetchAuthSession } from 'aws-amplify/auth';

const baseUrl = import.meta.env.VITE_API_BASE_URL ?? '/api';

async function authHeader(): Promise<HeadersInit> {
  try {
    const session = await fetchAuthSession();
    const token = session.tokens?.idToken?.toString();
    return token ? { Authorization: `Bearer ${token}` } : {};
  } catch {
    return {};
  }
}

/**
 * Thrown when the API returns a non-2xx response. Carries the HTTP status and
 * (when available) the parsed RFC 7807 problem-details body so UI code can
 * branch on `.status === 402` and pull `.detail` for human-readable text.
 */
export class ApiError extends Error {
  status: number;
  detail?: string;
  title?: string;

  constructor(status: number, statusText: string, body: unknown) {
    const problem = (body && typeof body === 'object' ? body : {}) as Record<string, unknown>;
    const title = typeof problem.title === 'string' ? problem.title : undefined;
    const detail = typeof problem.detail === 'string' ? problem.detail : undefined;
    const errorField = typeof problem.error === 'string' ? problem.error : undefined;
    super(detail ?? errorField ?? title ?? `${status} ${statusText}`);
    this.name = 'ApiError';
    this.status = status;
    this.title = title;
    this.detail = detail ?? errorField;
  }
}

async function parseBody(res: Response): Promise<unknown> {
  try {
    const text = await res.text();
    if (!text) {
      return null;
    }

    try {
      return JSON.parse(text);
    } catch {
      return text;
    }
  } catch {
    return null;
  }
}

export async function apiGet<T>(path: string): Promise<T> {
  const headers = await authHeader();
  const res = await fetch(`${baseUrl}${path}`, { headers });
  if (!res.ok) {
    throw new ApiError(res.status, res.statusText, await parseBody(res));
  }

  return (await res.json()) as T;
}

export async function apiPost<T, B = unknown>(path: string, body: B): Promise<T> {
  const headers = await authHeader();
  const res = await fetch(`${baseUrl}${path}`, {
    method: 'POST',
    headers: { ...headers, 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
  });
  if (!res.ok) {
    throw new ApiError(res.status, res.statusText, await parseBody(res));
  }

  return (await res.json()) as T;
}

export async function apiDelete(path: string): Promise<void> {
  const headers = await authHeader();
  const res = await fetch(`${baseUrl}${path}`, {
    method: 'DELETE',
    headers,
  });
  if (!res.ok) {
    throw new ApiError(res.status, res.statusText, await parseBody(res));
  }
}

export async function apiPut<T, B = unknown>(path: string, body: B): Promise<T> {
  const headers = await authHeader();
  const res = await fetch(`${baseUrl}${path}`, {
    method: 'PUT',
    headers: { ...headers, 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
  });
  if (!res.ok) {
    throw new ApiError(res.status, res.statusText, await parseBody(res));
  }

  return (await res.json()) as T;
}

export interface MeResponse {
  tenantId: string;
  email: string;
  status: string;
  brokerageName: string | null;
  createdAt: string;
}

export interface FeedSummary {
  feedId: string;
  label: string;
  status: string;
  lastSyncedAt: string | null;
  lastModificationTimestamp: string | null;
  lastSyncError: string | null;
  createdAt: string;
}

export interface ApiKeySummary {
  id: string;
  label: string;
  keyPrefix: string;
  createdAt: string;
  lastUsedAt: string | null;
  revokedAt: string | null;
  isRevoked: boolean;
}

export interface ApiKeyCreated extends ApiKeySummary {
  apiKey: string;
}
